import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

// ================================================================================
// FACILITY SERVICE — TB testing/treatment/support facility finder.
// Public GET (verified only) + admin CRUD. "Near me" only returns facilities
// that actually have geocoded coordinates — no invented distances.
// ================================================================================

export type FacilityType =
  | 'DMC' | 'NIKSHAY_CENTER' | 'TERTIARY_CARE_HOSPITAL' | 'DISTRICT_HOSPITAL'
  | 'CHC' | 'PHC' | 'CBNAAT_LAB' | 'DRTB_CENTER' | 'ART_CENTER'
  | 'GENERAL_HOSPITAL' | 'OTHER'

export type FacilityStatus = 'PENDING' | 'VERIFIED' | 'ARCHIVED'

export interface Facility {
  id: number
  name: string
  facilityType: FacilityType
  address?: string
  district?: string
  taluka?: string
  city?: string
  state?: string
  pincode?: string
  phone?: string
  email?: string
  latitude?: number | null
  longitude?: number | null
  servicesOffered?: string
  operatingHours?: string
  source?: string
  status: FacilityStatus
  isVerified?: boolean
  createdAt?: string
  distanceKm?: number | null
  /** Language actually served by the API — the requested one if a translation exists, else "en". */
  matchedLanguage?: string
  /** Languages this facility is available in; always includes "en" (the canonical row). */
  availableLanguages?: string[]
}

export interface FacilityRequest {
  name: string
  facilityType: FacilityType
  address?: string
  district?: string
  taluka?: string
  city?: string
  state?: string
  pincode?: string
  phone?: string
  email?: string
  latitude?: number | null
  longitude?: number | null
  servicesOffered?: string
  operatingHours?: string
  source?: string
  status?: FacilityStatus
  isVerified?: boolean
}

export interface FacilityStats {
  TOTAL: number
  GEOCODED: number
  PENDING: number
  VERIFIED: number
  ARCHIVED: number
}

// ---- public ----

export async function listFacilities(params: {
  type?: string
  state?: string
  district?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Facility>> {
  const qs = new URLSearchParams()
  if (params.type) qs.set('type', params.type)
  if (params.state) qs.set('state', params.state)
  if (params.district) qs.set('district', params.district)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 12))
  const res = await api.get<ApiResponse<PaginatedResponse<Facility>>>(`/facilities?${qs.toString()}`)
  return res.data.data
}

export async function nearbyFacilities(lat: number, lng: number, radius = 50, limit = 20): Promise<Facility[]> {
  const qs = new URLSearchParams({ lat: String(lat), lng: String(lng), radius: String(radius), limit: String(limit) })
  const res = await api.get<ApiResponse<Facility[]>>(`/facilities/nearby?${qs.toString()}`)
  return res.data.data
}

export async function getFacility(id: number): Promise<Facility> {
  const res = await api.get<ApiResponse<Facility>>(`/facilities/${id}`)
  return res.data.data
}

/**
 * Unified "Find a TB Centre" search: free-text q (PIN / city / district / taluka /
 * facility name) with optional type/state/district/taluka filters and optional
 * geolocation sort. Returns up to `limit` (default 30, max 60) relevance- or
 * distance-ranked verified facilities.
 */
export async function findFacilities(params: {
  q?: string
  taluka?: string
  district?: string
  state?: string
  type?: string
  lat?: number
  lng?: number
  radius?: number
  limit?: number
}): Promise<Facility[]> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.taluka) qs.set('taluka', params.taluka)
  if (params.district) qs.set('district', params.district)
  if (params.state) qs.set('state', params.state)
  if (params.type) qs.set('type', params.type)
  if (params.lat != null) qs.set('lat', String(params.lat))
  if (params.lng != null) qs.set('lng', String(params.lng))
  if (params.radius != null) qs.set('radius', String(params.radius))
  qs.set('limit', String(params.limit ?? 30))
  const res = await api.get<ApiResponse<Facility[]>>(`/facilities/find?${qs.toString()}`)
  return res.data.data
}

/** Distinct state → district → talukas tree over verified facilities, for filter dropdowns. */
export type FacilityLocations = Record<string, Record<string, string[]>>

export async function listLocations(): Promise<FacilityLocations> {
  const res = await api.get<ApiResponse<FacilityLocations>>(`/facilities/locations`)
  return res.data.data
}

// ---- admin ----

export async function listFacilitiesAdmin(params: {
  status?: string
  type?: string
  q?: string
  page?: number
  size?: number
}): Promise<PaginatedResponse<Facility>> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.type) qs.set('type', params.type)
  if (params.q) qs.set('q', params.q)
  qs.set('page', String(params.page ?? 0))
  qs.set('size', String(params.size ?? 20))
  const res = await api.get<ApiResponse<PaginatedResponse<Facility>>>(`/facilities/admin?${qs.toString()}`)
  return res.data.data
}

export async function getFacilityStats(): Promise<FacilityStats> {
  const res = await api.get<ApiResponse<FacilityStats>>(`/facilities/stats`)
  return res.data.data
}

export async function createFacility(req: FacilityRequest): Promise<Facility> {
  const res = await api.post<ApiResponse<Facility>>('/facilities', req)
  return res.data.data
}

export async function updateFacility(id: number, req: FacilityRequest): Promise<Facility> {
  const res = await api.put<ApiResponse<Facility>>(`/facilities/${id}`, req)
  return res.data.data
}

export async function changeFacilityStatus(id: number, status: FacilityStatus): Promise<Facility> {
  const res = await api.put<ApiResponse<Facility>>(`/facilities/${id}/status?status=${status}`)
  return res.data.data
}

export async function deleteFacility(id: number): Promise<void> {
  await api.delete<ApiResponse<void>>(`/facilities/${id}`)
}

// ---- label maps ----

export const FACILITY_TYPE_LABELS: Record<FacilityType, string> = {
  DMC: 'Sputum microscopy centre (DMC)',
  NIKSHAY_CENTER: 'TB treatment centre (Ni-kshay Centre)',
  TERTIARY_CARE_HOSPITAL: 'Tertiary care hospital',
  DISTRICT_HOSPITAL: 'District hospital',
  CHC: 'Community health centre (CHC)',
  PHC: 'Primary health centre (PHC)',
  CBNAAT_LAB: 'Rapid molecular test lab (CBNAAT)',
  DRTB_CENTER: 'Drug-resistant TB centre (DR-TB Centre)',
  ART_CENTER: 'HIV care centre (ART Centre)',
  GENERAL_HOSPITAL: 'General hospital',
  OTHER: 'Other',
}

export const FACILITY_STATUS_LABELS: Record<FacilityStatus, string> = {
  PENDING: 'Pending verification',
  VERIFIED: 'Verified',
  ARCHIVED: 'Archived',
}